import { cn, Divider, InfoPopover, Pill, Stack } from 'component-library';
import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { PlotStatusEnum, PlotType } from '@/api/rest/resources/types/plot';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { PlotTypeIcon } from '@/components';
import { DataGroup, DataStack, DataStackContent, DataStackLabel } from '@/components/DataLabel/DataLabel';
import { NO_RADIUS, useMapRadiusContext } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotLocation } from '@/hooks/usePlotLocation';
import { useScreenSize } from '@/hooks/useScreenSize';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { squareMetersToHectares } from '@/utils/plot';

import { useLandscapeViewContext } from '../LandscapeViewContextProvider';

const maxDisplayCropCount = 10;

export const PlotOverview: React.FC = () => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';

  const plot = usePlot().data;

  const isForest = plot.type === PlotType.TREE_COVER; // TODO: MVP-4724 - swap out with PlotType.FORESTRY
  const areaDisplay = `${useDisplayNumber(squareMetersToHectares(plot.area))} ${formatUnit(UnitEnum.ha)}`;

  const { isLandscapeActive } = useLandscapeViewContext();
  const { radius } = useMapRadiusContext();
  const radiusMeters = isNullish(radius) || radius === NO_RADIUS ? 0 : Number(radius) * 1000;
  const landscapeArea = radiusMeters ? Math.PI * radiusMeters * radiusMeters : 0;
  const landscapeAreaDisplay = `${useDisplayNumber(squareMetersToHectares(landscapeArea))} ${formatUnit(UnitEnum.ha)}`;

  const facts = usePlotFacts<number>([R1FactType.r1_deforestation_occurred_since_2020], plot.id);
  const deforestedPlotCount = getFactFromFactArray<number>(facts, R1FactType.r1_deforestation_occurred_since_2020);

  const plotType = plot.type ?? null;

  const location = usePlotLocation({});

  const crops =
    plotType === PlotType.CROPLAND && plot.crops && plot.crops.length > 0
      ? plot.crops.map((crop) => t(`global.crops.${crop}`)).sort()
      : undefined;

  const deforestationActivityText = useMemo(() => {
    if (isNullish(deforestedPlotCount?.value)) {
      if ([PlotStatusEnum.scheduled_for_analysis].includes(plot.status)) {
        return (
          <DataStackContent className='text-text-secondary'>
            {t('global.analysis.calculationInProgress')}
          </DataStackContent>
        );
      }
      return <DataStackContent className='text-text-secondary'>{t('global.analysis.noAnalysis')}</DataStackContent>;
    }
    const isOutdatedValue = deforestedPlotCount?.value !== null && plot.status !== PlotStatusEnum.analysed;
    const textColor = isOutdatedValue ? 'text-text-secondary' : 'text-text-primary';

    if (deforestedPlotCount?.value === 0) {
      return (
        <DataStackContent className={textColor}>
          {t('shared.ncaDetail.details.deforestation.noDeforestationActivityDetected')}
        </DataStackContent>
      );
    }
    if (deforestedPlotCount?.value === 1) {
      return (
        <DataStackContent className={textColor}>
          {t('shared.ncaDetail.details.deforestation.deforestationActivityDetected')}
        </DataStackContent>
      );
    }
    return <DataStackContent className='text-text-secondary'>{t('global.analysis.noAnalysis')}</DataStackContent>;
  }, [t, deforestedPlotCount, plot]);

  return (
    <PlotOverviewContainer>
      <DataGroup className='items-start'>
        <DataStack>
          <DataStackLabel>{t('global.plot.mapPopup.labels.plotName')}</DataStackLabel>
        </DataStack>
        <DataStackContent>{plot.name}</DataStackContent>
      </DataGroup>

      <Divider />

      <DataGroup className={cn('grid grid-cols-1 gap-6 md:grid-cols-2')}>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.plotSize')}</DataStackLabel>
          <DataStackContent>{areaDisplay}</DataStackContent>
        </DataStack>
        {isLandscapeActive && (
          <DataStack>
            <DataStackLabel>{t('shared.ncaDetail.details.labels.landscapeArea')}</DataStackLabel>
            <DataStackContent>{landscapeAreaDisplay}</DataStackContent>
          </DataStack>
        )}
        <DataStack>
          <DataStackLabel>{t('global.plot.plotLandType')}</DataStackLabel>
          <DataStackContent>
            <Stack direction='row' spacing={1}>
              <PlotTypeIcon type={plotType} size={20} />
              <span>{t(`global.plotTypes.${plotType}`)}</span>
            </Stack>
          </DataStackContent>
        </DataStack>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.location')}</DataStackLabel>
          <DataStackContent>{location}</DataStackContent>
        </DataStack>
        {crops && (
          <DataStack>
            <DataStackLabel>{t('shared.ncaDetail.details.labels.crops')}</DataStackLabel>
            <Stack className='flex flex-row flex-wrap gap-2'>
              {crops.slice(0, maxDisplayCropCount).map((crop, index) => (
                <Pill key={index} size='small' className='whitespace-nowrap bg-warning-light'>
                  {crop}
                </Pill>
              ))}
              {crops.length > maxDisplayCropCount && (
                <Pill key='...' size='small' className='bg-white whitespace-nowrap'>
                  {t('shared.ncaDetail.itemsMoreCount', {
                    count: crops.length - maxDisplayCropCount,
                  })}
                </Pill>
              )}
            </Stack>
          </DataStack>
        )}
      </DataGroup>
      {!isForest && (
        <>
          <Divider className='mx-6' />
          <DataGroup>
            <DataStack>
              <DataStackLabel>
                <Stack direction='row' spacing={1} className='items-center'>
                  {t('shared.ncaDetail.details.deforestation.deforestationActivity')}
                  {!isSmallScreen && (
                    <InfoPopover position='top' body={t('shared.ncaDetail.details.deforestation.popoverBody')} />
                  )}
                </Stack>
              </DataStackLabel>
              {deforestationActivityText}
            </DataStack>
          </DataGroup>
        </>
      )}
    </PlotOverviewContainer>
  );
};

export const PlotOverviewContainer = ({ children }: { children: ReactNode }) => {
  return (
    <div
      className='grid-row grid auto-rows-min items-start rounded-3xl bg-white-100 border border-neutral-black-12'
      data-testid='plot-overview'
    >
      {children}
    </div>
  );
};
